import { useEffect, useState } from 'react';
import { Plus, Save } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import type { ReportRef, ScreenDef } from '@/lib/types';
import type { GridColumn } from '@/grid/types';
import { EditGrid, EditRow, isDirty, newKey, toEditRows } from '@/grid/EditGrid';
import { Dialog } from '@/ui/Dialog';
import { Spinner } from '@/ui/Spinner';
import { promptDialog } from '@/ui/confirm';
import { ReportMenu } from '@/reports/ReportMenu';

type Row = Record<string, any>;

/**
 * Dizayn listelerinin Delphi araç çubuğundaki ek düğmeleri (Web_Screen.AYAR):
 *  personelIslem   → Unit2 "Prim Günü" (Unit2_5) ve "Vizite" (Unit2_8)
 *  alternatifParca → Firin\Unit18 "Alternatif Parça Listesine Ekle" (Unit18_5)
 */
export function ListeEkIslemleri({ screen, row }: { screen: ScreenDef; row: Row | null }) {
  const [ac, setAc] = useState<null | 'prim' | 'vizite'>(null);
  const s = screen.settings ?? {};
  const id = row ? Number(row.ID) : 0;
  const alternatif = async () => {
    const kod = await promptDialog('Alternatif stok kodu', { title: `Alternatif Parça Girişi · ${String(row?.STOK_KODU ?? '').trim()}` });
    if (!kod) return;
    try {
      const r = await api<{ mesaj: string | null }>(`/personel-islem/stok/${id}/alternatif`, { body: { stokKodu: kod } });
      if (r.mesaj) toast.success(r.mesaj); else toast.info('Değişiklik yapılmadı (kayıt zaten alternatif listesinde)');
    } catch (e: any) { toast.error(e.message); }
  };
  return (
    <>
      {s.personelIslem && <>
        <button className="btn" disabled={!row} onClick={() => setAc('prim')}>Prim Günü</button>
        <button className="btn" disabled={!row} onClick={() => setAc('vizite')}>Vizite</button>
      </>}
      {s.alternatifParca && <button className="btn" disabled={!row || !screen.rights.update} onClick={alternatif}>Alternatif Parça Ekle</button>}
      {ac === 'prim' && <PrimDialog perId={id} onClose={() => setAc(null)} />}
      {ac === 'vizite' && <ViziteDialog perId={id} onClose={() => setAc(null)} onPrim={() => setAc('prim')} />}
    </>
  );
}

const PRIM_COLS: GridColumn[] = [
  { key: 'YIL', caption: 'Yıl', width: 70, sqlType: 'int', editor: 'number' },
  { key: 'AY', caption: 'Ay', width: 60, sqlType: 'int', editor: 'number' },
  { key: 'PRIM', caption: 'Prim Gün Sayısı', width: 110, sqlType: 'float', editor: 'number', total: 'sum' },
  { key: 'TUTAR', caption: 'Maaş', width: 110, sqlType: 'float', editor: 'number', mask: '#,##0.00', total: 'sum' },
  { key: 'EK_ODEME', caption: 'Ek Ödeme', width: 110, sqlType: 'float', editor: 'number', mask: '#,##0.00', total: 'sum' },
  { key: 'MAHKEME_TUTAR', caption: 'İdari Prim ve Ödemeler', width: 150, sqlType: 'float', editor: 'number', mask: '#,##0.00', total: 'sum' },
  { key: 'ACIKLAMA', caption: 'Açıklama', width: 220 },
];

/** Unit2_5 "Primleri": personelin Personel_Prim satırları (Satırı Sil menüsü dahil) */
function PrimDialog({ perId, onClose }: { perId: number; onClose: () => void }) {
  const [d, setD] = useState<{ personel: Row; rights: ScreenDef['rights'] } | null>(null);
  const [rows, setRows] = useState<EditRow[]>([]);
  const [deleted, setDeleted] = useState<number[]>([]);
  const [sel, setSel] = useState<number | null>(null);
  const yukle = () => api<{ personel: Row; rows: Row[]; rights: ScreenDef['rights'] }>(`/personel-islem/${perId}/prim`).then((r) => { setD(r); setRows(toEditRows(r.rows)); setDeleted([]); }).catch((e) => toast.error(e.message));
  useEffect(() => { yukle(); }, [perId]); // eslint-disable-line react-hooks/exhaustive-deps
  const kaydet = async () => {
    try {
      await api(`/personel-islem/${perId}/prim`, { body: { rows: rows.filter(isDirty).map((x) => ({ row: x.row, original: x.original })), deleted } });
      toast.success('Kaydedildi');
      yukle();
    } catch (e: any) { toast.error(e.message); }
  };
  const ro = !d?.rights.update;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Primleri · ${String(d?.personel.P_KODU ?? '').trim()} ${String(d?.personel.P_ADI ?? '').trim()}`} className="h-[70vh] w-[min(980px,96vw)]"
      footer={<><button className="btn" onClick={onClose}>Kapat</button><button className="btn btn-primary" disabled={ro || !(rows.some(isDirty) || deleted.length)} onClick={kaydet}><Save size={14} /> Kaydet</button></>}>
      {!d ? <Spinner /> : (
        <EditGrid className="h-full" columns={PRIM_COLS} rows={rows} selected={sel} onSelect={setSel} readOnly={ro} layoutKey="PersonelPrim" deleteLabel="Satırı Sil"
          toolbar={!ro && <button className="btn" onClick={() => { setRows((r) => [...r, { _k: newKey(), row: { YIL: new Date().getFullYear(), AY: new Date().getMonth() + 1 }, original: null }]); setSel(rows.length); }}><Plus size={14} /> Satır Ekle</button>}
          onChange={(i, p) => setRows((rs) => rs.map((x, k) => (k === i ? { ...x, row: { ...x.row, ...p } } : x)))}
          onDelete={(i) => { const x = rows[i]; if (x.original?.ID) setDeleted((dd) => [...dd, Number(x.original!.ID)]); setRows((rs) => rs.filter((_, k) => k !== i)); setSel(null); }} />
      )}
    </Dialog>
  );
}

/** Unit2_8 "Personel Vizite İşlemleri" — iş kazası bilgileri (geçici Personel_Is_Kazasi), Vizite raporları */
const VIZITE: [string, string][] = [
  ['A', 'Olduğu Tarihte Çalışan İşçi Sayısı'], ['B', 'Olduğu Sırada Sigortalının Yaptığı İş ve Bu İşin Mahiyeti'], ['C', 'Oluş Şekli'],
  ['D', 'Meydana Geldiği Yer'], ['E', 'Olduğu Tarih ve Saat'], ['F', 'Olduğu Günün İş Başı Saati'],
  ['G', 'Tanık 1 — Adı'], ['H', 'Tanık 1 — Soyadı'], ['I', 'Tanık 2 — Adı'], ['J', 'Tanık 2 — Soyadı'], ['K', 'Tanık 3 — Adı'], ['L', 'Tanık 3 — Soyadı'],
];
function ViziteDialog({ perId, onClose, onPrim }: { perId: number; onClose: () => void; onPrim: () => void }) {
  const [d, setD] = useState<{ personel: Row; reports: ReportRef[] } | null>(null);
  const [v, setV] = useState<Row>({});
  useEffect(() => { api<{ personel: Row; reports: ReportRef[] }>(`/personel-islem/${perId}/vizite`).then(setD).catch((e) => toast.error(e.message)); }, [perId]);
  const kaydet = async () => {
    try { await api(`/personel-islem/${perId}/vizite`, { body: { row: v } }); return true; } catch (e: any) { toast.error(e.message); return false; }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()} title={`Personel Vizite İşlemleri · ${String(d?.personel.P_ADI ?? '').trim()}`} className="w-[min(760px,96vw)]"
      footer={<>
        <button className="btn mr-auto" onClick={onPrim}>Prim Günü</button>
        <button className="btn" onClick={onClose}>Çıkış</button>
        {d && <ReportMenu reports={d.reports} row={d.personel} params={{ 'Personel Kodu': String(d.personel.P_KODU ?? '').trim() }} beforeOpen={kaydet} label="Yazdır" />}
      </>}>
      {!d ? <Spinner /> : (
        <div className="grid gap-x-4 gap-y-2 p-1 sm:grid-cols-2">
          <div className="sm:col-span-2 text-[12px] font-semibold uppercase tracking-wide muted">B — İş kazasının</div>
          {VIZITE.map(([k, l]) => (
            <label key={k} className="flex flex-col gap-1"><span className="h-label">{l}</span>
              <input className="inp" maxLength={100} value={v[k] ?? ''} onChange={(e) => setV((x) => ({ ...x, [k]: e.target.value }))} /></label>
          ))}
        </div>
      )}
    </Dialog>
  );
}
